import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getDashboardStats } from '../services/adminApi';
import { formatCurrency } from '../utils/formatters';

const KPI_CARDS = [
  { getter: (s) => s?.interns?.total ?? 0, label: 'Total Interns', sublabel: (s) => `${s?.interns?.approved ?? 0} approved, ${s?.interns?.pending ?? 0} pending`, icon: PeopleIcon, format: 'number' },
  { getter: (s) => s?.bookings?.total ?? 0, label: 'Total Bookings', sublabel: (s) => `${s?.bookings?.active ?? 0} active`, icon: EventIcon, format: 'number' },
  { getter: (s) => s?.clients?.total ?? 0, label: 'Total Clients', sublabel: () => '', icon: AssignmentIcon, format: 'number' },
  { getter: (s) => s?.revenue?.total ?? 0, label: 'Total Revenue', sublabel: (s) => `${s?.payments?.length ?? 0} payments`, icon: AttachMoneyIcon, format: 'currency' },
];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getDashboardStats()
      .then((data) => setStats(data))
      .catch((err) => setError(err.message || 'Failed to load dashboard stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {KPI_CARDS.map((card) => {
          const Icon = card.icon;
          const value = card.getter(stats);
          const sub = card.sublabel(stats);

          return (
            <Grid item xs={12} sm={6} md={6} lg={3} key={card.label}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 0 }}>
                    {card.label}
                  </Typography>
                  <Icon sx={{ color: 'primary.main', fontSize: 28, flexShrink: 0 }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    // Responsive font size: scales between the container width
                    // (so "$1,234,567" doesn't clip on a 200px-wide card).
                    fontSize: 'clamp(1.05rem, 4vw, 2.125rem)',
                    lineHeight: 1.2,
                    // Graceful degradation if text STILL overflows (huge values)
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    
                  }}
                  title={card.format === 'currency' ? formatCurrency(value) : value.toLocaleString()}
                >
                  {card.format === 'currency' ? formatCurrency(value) : value.toLocaleString()}
                </Typography>
                {sub && (
                  <Typography variant="caption" color="text.secondary">{sub}</Typography>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stats?.recent_activity?.length
                ? stats.recent_activity.map((item, i) => (
                    <Box key={i} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      {item.description || item.message || JSON.stringify(item)}
                    </Box>
                  ))
                : 'No recent activity to display.'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Pending Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stats?.pending_interns != null && (
                <Box sx={{ py: 0.5 }}>{stats.pending_interns} intern(s) awaiting review</Box>
              )}
              {stats?.pending_bookings != null && (
                <Box sx={{ py: 0.5 }}>{stats.pending_bookings} booking(s) pending confirmation</Box>
              )}
              {stats?.pending_assessments != null && (
                <Box sx={{ py: 0.5 }}>{stats.pending_assessments} assessment(s) pending review</Box>
              )}
              {!stats?.pending_interns && !stats?.pending_bookings && !stats?.pending_assessments && (
                'No pending actions.'
              )}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
